// JournalTab.js - Huvudkomponent för journal-fliken i patientvyn

import React, { useState, useEffect, useCallback } from 'react';
import JournalListItem from './JournalListItem';
import JournalContent from './JournalContent';
import NewJournalModal from './NewJournalModal';
import ConfirmDialog from '../common/ConfirmDialog';
import { getJournalEntries, getJournalEntry, deleteJournalEntry, signJournalEntry, isJournalContentEmpty } from './journalApi';
import { waitForPendingSave } from './useDraftAutosave';
import './JournalTab.css';

// Referenspanelen (Spår J2) visas som tredje kolumn från denna bredd,
// annars som hopfällbar panel ovanför editorn.
const WIDE_REFERENCE_QUERY = '(min-width: 1400px)';

const useMediaQuery = (query) => {
  const get = () => (typeof window !== 'undefined' && window.matchMedia
    ? window.matchMedia(query).matches
    : false);
  const [matches, setMatches] = useState(get);
  useEffect(() => {
    if (!window.matchMedia) return undefined;
    const mql = window.matchMedia(query);
    const onChange = () => setMatches(mql.matches);
    onChange();
    mql.addEventListener('change', onChange);
    return () => mql.removeEventListener('change', onChange);
  }, [query]);
  return matches;
};

const JournalTab = ({ patientId, patient, initialEntryId = null, onInitialEntryConsumed,
                      autoOpenNewJournal = false, onAutoOpenConsumed, appointmentIdForNewJournal = null }) => {
  // State för journallista
  const [journals, setJournals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // State för valda journaler (multi-select)
  const [selectedIds, setSelectedIds] = useState([]);
  const [selectedJournals, setSelectedJournals] = useState([]);
  const [loadingContent, setLoadingContent] = useState(false);

  // State för ny journal-modal
  const [showNewJournalModal, setShowNewJournalModal] = useState(false);
  // Bokningen som modalen kopplar posten till — bara när modalen öppnats
  // från en bokning (kalender/startsida), inte vid manuellt "Ny journal"
  const [modalAppointmentId, setModalAppointmentId] = useState(null);

  // Diktering ska auto-starta för nyskapad journal (från "Starta inspelning")
  const [autoRecordEntryId, setAutoRecordEntryId] = useState(null);

  // State för redigering
  const [editingJournalId, setEditingJournalId] = useState(null);
  const [savingJournal, setSavingJournal] = useState(false);

  // Skrivskyddad referens bredvid/ovanför editorn (Spår J2) — skild från
  // selectedIds/editingJournalId så att redigeringen fortsätter ostörd.
  const [referenceEntryId, setReferenceEntryId] = useState(null);
  const [referenceJournal, setReferenceJournal] = useState(null);
  const [referenceLoading, setReferenceLoading] = useState(false);
  const [referenceCollapsed, setReferenceCollapsed] = useState(false);
  const [listCollapsed, setListCollapsed] = useState(false);
  const isWide = useMediaQuery(WIDE_REFERENCE_QUERY);

  // Bekräftelse-dialog för radering av utkast
  const [deleteTargetId, setDeleteTargetId] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // Patientens obetalda besök (från patient.appointments) — driver
  // "Betala besök"-knappen i journalvyn
  const unpaidAppointments = React.useMemo(
    () => (patient?.appointments || []).filter(
      a => a.patient_id && a.is_paid === false && a.status !== 'cancelled'
    ),
    [patient]
  );

  // Ladda journaler för patient
  const loadJournals = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await getJournalEntries(patientId);
      setJournals(data.entries || []);
    } catch (err) {
      console.error('Error loading journals:', err);
      if (err.response?.status === 403) {
        setError('Journalmodulen är inte aktiverad för denna klinik');
      } else {
        setError('Kunde inte ladda journaler');
      }
    } finally {
      setLoading(false);
    }
  }, [patientId]);

  useEffect(() => {
    loadJournals();
  }, [loadJournals]);

  // Auto-öppna "Ny journal"-modalen (t.ex. från kalenderns högerklicksmeny)
  useEffect(() => {
    if (autoOpenNewJournal) {
      setModalAppointmentId(appointmentIdForNewJournal);
      setShowNewJournalModal(true);
      if (onAutoOpenConsumed) {
        onAutoOpenConsumed();
      }
    }
  }, [autoOpenNewJournal, onAutoOpenConsumed, appointmentIdForNewJournal]);

  // Förvälj en journal (t.ex. via klick i tidslinjen på Översikt-fliken).
  // Notifiera föräldern så propet nollställs - annars kan samma post inte
  // öppnas igen (oförändrat prop => effekten kör inte om).
  useEffect(() => {
    if (initialEntryId) {
      setSelectedIds([initialEntryId]);
      setEditingJournalId(null);
      if (onInitialEntryConsumed) {
        onInitialEntryConsumed();
      }
    }
  }, [initialEntryId, onInitialEntryConsumed]);

  // Ladda innehåll för valda journaler.
  // opts.silent: hämta om utan laddningsvy — editorn får inte avmonteras
  // mitt i en redigering (t.ex. efter betalning av besöket).
  const loadSelectedJournals = useCallback(async (ids, opts = {}) => {
    if (ids.length === 0) {
      setSelectedJournals([]);
      return;
    }

    try {
      if (!opts.silent) setLoadingContent(true);
      // Vänta in ev. autosparning (t.ex. från en editor som just avmonterats)
      // så att vi inte läser gammalt innehåll.
      const journalPromises = ids.map(async (id) => {
        await waitForPendingSave(id);
        return getJournalEntry(id);
      });
      const results = await Promise.all(journalPromises);
      // Sortera i samma ordning som ids
      const sorted = ids.map(id => results.find(j => j.entry_id === id)).filter(Boolean);
      if (opts.silent) {
        // En autosparning kan ha landat medan GET:en var ute — behåll den
        // lokala kopian om den är nyare (annars går version/innehåll bakåt).
        setSelectedJournals(prev => sorted.map(j => {
          const local = prev.find(p => p.entry_id === j.entry_id);
          return local && (local.version || 0) > (j.version || 0)
            ? { ...j, content: local.content, version: local.version }
            : j;
        }));
      } else {
        setSelectedJournals(sorted);
      }
    } catch (err) {
      console.error('Error loading journal content:', err);
    } finally {
      setLoadingContent(false);
    }
  }, []);

  useEffect(() => {
    loadSelectedJournals(selectedIds);
  }, [selectedIds, loadSelectedJournals]);

  // Ladda referensjournalen (skrivskyddad, Spår J2)
  useEffect(() => {
    if (referenceEntryId == null) {
      setReferenceJournal(null);
      return undefined;
    }
    let cancelled = false;
    setReferenceLoading(true);
    (async () => {
      try {
        await waitForPendingSave(referenceEntryId);
        const data = await getJournalEntry(referenceEntryId);
        if (!cancelled) setReferenceJournal(data);
      } catch (err) {
        console.error('Kunde inte ladda referensjournal:', err);
        if (!cancelled) setReferenceEntryId(null);
      } finally {
        if (!cancelled) setReferenceLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [referenceEntryId]);

  const showReference = useCallback((entryId) => {
    setReferenceEntryId(entryId);
    setReferenceCollapsed(false);
  }, []);

  const closeReference = useCallback(() => {
    setReferenceEntryId(null);
    setListCollapsed(false);
  }, []);

  // Hantera klick på journal (single select).
  // Under redigering byter ett klick på en annan post INTE redigeringen —
  // posten visas i stället skrivskyddad i referenspanelen (Spår J2).
  const handleJournalClick = useCallback((entryId) => {
    if (editingJournalId != null) {
      if (entryId !== editingJournalId) showReference(entryId);
      return;
    }
    setSelectedIds([entryId]);
    setEditingJournalId(null);
    if (entryId === referenceEntryId) setReferenceEntryId(null);
  }, [editingJournalId, referenceEntryId, showReference]);

  // Hantera shift+klick för multi-select
  const handleShiftClick = useCallback((entryId) => {
    // Under redigering: samma som vanligt klick (referens), inget flerval
    if (editingJournalId != null) {
      handleJournalClick(entryId);
      return;
    }
    if (selectedIds.length === 0) {
      setSelectedIds([entryId]);
      return;
    }

    // Hitta index för första valda och klickade
    const journalIds = journals.map(j => j.entry_id);
    const firstSelectedIdx = journalIds.indexOf(selectedIds[0]);
    const clickedIdx = journalIds.indexOf(entryId);

    if (firstSelectedIdx === -1 || clickedIdx === -1) {
      setSelectedIds([entryId]);
      return;
    }

    // Välj alla mellan första och klickade
    const start = Math.min(firstSelectedIdx, clickedIdx);
    const end = Math.max(firstSelectedIdx, clickedIdx);
    const newSelectedIds = journalIds.slice(start, end + 1);
    setSelectedIds(newSelectedIds);
    setEditingJournalId(null);
  }, [selectedIds, journals, editingJournalId, handleJournalClick]);

  // Hantera "Ny journal"
  const handleNewJournal = () => {
    setModalAppointmentId(null);
    setShowNewJournalModal(true);
  };

  // Hantera journal skapad (opts.autoRecord = starta dikteringen direkt)
  const handleJournalCreated = (newJournal, opts = {}) => {
    // Sortera in på rätt plats (nyast först, som backend). En backdaterad
    // anteckning (Spår 5) är inte nödvändigtvis nyast i listan.
    setJournals(prev => [newJournal, ...prev].sort(
      (a, b) => new Date(b.created_at) - new Date(a.created_at)
    ));
    setSelectedIds([newJournal.entry_id]);
    setShowNewJournalModal(false);
    // Starta redigering direkt
    setEditingJournalId(newJournal.entry_id);
    if (opts.autoRecord) {
      setAutoRecordEntryId(newJournal.entry_id);
    }
  };

  // Hantera redigera
  // Hämta posten färskt innan redigeringen börjar, så att editorn utgår från
  // serverns senaste innehåll och version (t.ex. en diktering som blev klar
  // efter att editorn stängts) — annars falsk konflikt eller gammal text.
  const handleEdit = async (entryId) => {
    try {
      await waitForPendingSave(entryId);
      const fresh = await getJournalEntry(entryId);
      setSelectedJournals(prev => prev.map(j => (j.entry_id === entryId ? fresh : j)));
      if (fresh.status !== 'draft') return;
    } catch (err) {
      console.error('Kunde inte hämta journalen inför redigering:', err);
    }
    setEditingJournalId(entryId);
  };

  // Autosparat utkast (Spår J): håll den lokala kopian i synk så att läsläget
  // efter "Stäng" visar det sparade innehållet och versionen stämmer.
  const handleAutosaved = useCallback((entryId, saved) => {
    setSelectedJournals(prev =>
      prev.map(j => (j.entry_id === entryId
        ? {
            ...j,
            content: saved.content,
            version: saved.version || j.version,
            updated_at: saved.updated_at || j.updated_at,
          }
        : j))
    );
  }, []);

  // Hantera signera.
  // `content` skickas med från editorn (aktuellt editContent) så att servern
  // sparar OCH signerar atomärt — annars kunde text tappas när "Signera"
  // trycktes utan att spara först (Spår F, bekräftad datadataförlust).
  const handleSign = async (entryId, content, expectedVersion) => {
    // Fall tillbaka på lagrat innehåll om editorn inte skickade med något
    // (t.ex. signering direkt från läsläget).
    const effectiveContent =
      content !== undefined
        ? content
        : selectedJournals.find(j => j.entry_id === entryId)?.content;

    // Skyddsnät i frontend: vägra signera tom journal (backend gör samma koll).
    if (isJournalContentEmpty(effectiveContent)) {
      alert('Kan inte signera en tom journal. Skriv något innehåll först.');
      return;
    }

    if (!window.confirm('Är du säker på att du vill signera denna journal? Den kommer inte kunna redigeras efter signering.')) {
      return;
    }

    try {
      setSavingJournal(true);
      // Spara innehållet i samma anrop som signeringen (atomärt server-side).
      const updated = await signJournalEntry(entryId, effectiveContent, expectedVersion);
      // Uppdatera i listan
      setJournals(prev =>
        prev.map(j => j.entry_id === entryId ? { ...j, status: 'signed', signed_at: updated.signed_at } : j)
      );
      // Uppdatera i valda
      setSelectedJournals(prev =>
        prev.map(j => j.entry_id === entryId ? updated : j)
      );
      setEditingJournalId(null);
    } catch (err) {
      if (err.response?.status === 409) {
        // Utkastet har ändrats någon annanstans — signera inte äldre text
        alert(err.response.data?.error || 'Utkastet har ändrats någon annanstans — ladda om innan du signerar.');
        if (editingJournalId !== entryId) loadSelectedJournals(selectedIds, { silent: true });
        return { conflict: true };
      }
      console.error('Error signing journal:', err);
      alert(err.response?.data?.error || 'Kunde inte signera journalen');
    } finally {
      setSavingJournal(false);
    }
  };

  // Hantera ta bort — öppnar bekräftelse-dialogen
  const handleDelete = (entryId) => {
    setDeleteTargetId(entryId);
  };

  // Utför raderingen efter bekräftelse
  const confirmDelete = async () => {
    if (deleteTargetId == null) return;

    setDeleting(true);
    try {
      await deleteJournalEntry(deleteTargetId);
      setJournals(prev => prev.filter(j => j.entry_id !== deleteTargetId));
      if (referenceEntryId === deleteTargetId) setReferenceEntryId(null);
      setSelectedIds(prev => prev.filter(id => id !== deleteTargetId));
      setSelectedJournals(prev => prev.filter(j => j.entry_id !== deleteTargetId));
      setDeleteTargetId(null);
    } catch (err) {
      console.error('Error deleting journal:', err);
      alert(err.response?.data?.error || 'Kunde inte ta bort journalen');
    } finally {
      setDeleting(false);
    }
  };

  // Hantera avbryt redigering
  const handleCancelEdit = () => {
    setEditingJournalId(null);
  };

  // Skrivskyddad referenspanel ("Tidigare anteckning", Spår J2)
  const renderReference = (placement) => {
    const ref = referenceJournal && referenceJournal.entry_id === referenceEntryId
      ? referenceJournal
      : null;
    const title = ref
      ? `${ref.display_name || ref.template_name || 'Journal'} · ${new Date(ref.created_at).toLocaleDateString('sv-SE')}`
      : '';
    const collapsed = placement === 'top' && referenceCollapsed;
    return (
      <div className={`journal-reference-panel journal-reference-panel--${placement}${collapsed ? ' collapsed' : ''}`}>
        <div className="journal-reference-header">
          <button
            type="button"
            className="journal-reference-toggle"
            onClick={() => placement === 'top' && setReferenceCollapsed(c => !c)}
            disabled={placement !== 'top'}
            aria-expanded={!collapsed}
          >
            {placement === 'top' && (
              <i className={`fas fa-chevron-${collapsed ? 'right' : 'down'}`}></i>
            )}
            <i className="fas fa-book-open"></i>
            <span className="journal-reference-label">Tidigare anteckning</span>
            {title && <span className="journal-reference-title">{title}</span>}
          </button>
          <span className="journal-reference-badge">Skrivskyddad</span>
          <button
            type="button"
            className="journal-reference-close"
            onClick={closeReference}
            title="Stäng referensen"
            aria-label="Stäng referensen"
          >
            <i className="fas fa-times"></i>
          </button>
        </div>
        {!collapsed && (
          <div className="journal-reference-body">
            {referenceLoading || !ref ? (
              <div className="journal-content-loading">
                <i className="fas fa-spinner fa-spin"></i> Laddar...
              </div>
            ) : (
              <JournalContent
                key={ref.entry_id}
                journal={ref}
                isEditing={false}
                readOnly
                onEdit={() => {}}
                onSign={() => {}}
                onDelete={() => {}}
                onCancel={() => {}}
              />
            )}
          </div>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="journal-tab loading">
        <i className="fas fa-spinner fa-spin"></i> Laddar journaler...
      </div>
    );
  }

  if (error) {
    return (
      <div className="journal-tab error">
        <i className="fas fa-exclamation-triangle"></i> {error}
      </div>
    );
  }

  return (
    <div className={`journal-tab${referenceEntryId != null && isWide ? ' journal-tab--reference-wide' : ''}`}>
      {/* Toolbar */}
      <div className="journal-toolbar">
        <button className="btn btn-primary btn-sm" onClick={handleNewJournal}>
          <i className="fas fa-plus"></i> Ny journal
        </button>
        <div className="journal-info">
          {journals.length} {journals.length === 1 ? 'journal' : 'journaler'}
          {selectedIds.length > 1 && ` (${selectedIds.length} valda)`}
        </div>
      </div>

      {/* Split view */}
      <div
        className={[
          'journal-split-view',
          referenceEntryId != null && isWide ? 'journal-split-view--reference-wide' : '',
          referenceEntryId != null && isWide && listCollapsed ? 'journal-split-view--list-collapsed' : '',
        ].filter(Boolean).join(' ')}
      >
        {/* Vänsterpanel - Journallista */}
        <div className="journal-list-panel">
          {referenceEntryId != null && isWide && (
            <button
              type="button"
              className="journal-list-collapse-btn"
              onClick={() => setListCollapsed(c => !c)}
              title={listCollapsed ? 'Visa listan' : 'Fäll ihop listan'}
            >
              <i className={`fas fa-chevron-${listCollapsed ? 'right' : 'left'}`}></i>
              {!listCollapsed && <span> Fäll ihop listan</span>}
            </button>
          )}
          {journals.length === 0 ? (
            <div className="journal-list-empty">
              <i className="fas fa-file-medical"></i>
              <p>Inga journaler</p>
              <span>Klicka "Ny journal" för att skapa en</span>
            </div>
          ) : (
            <div className="journal-list">
              {journals.map(journal => (
                <JournalListItem
                  key={journal.entry_id}
                  journal={journal}
                  isSelected={selectedIds.includes(journal.entry_id)}
                  isReference={referenceEntryId === journal.entry_id}
                  onClick={handleJournalClick}
                  onShiftClick={handleShiftClick}
                  onShowBeside={
                    selectedIds.length === 1 && !selectedIds.includes(journal.entry_id)
                      ? showReference
                      : undefined
                  }
                />
              ))}
            </div>
          )}
        </div>

        {/* Högerpanel - Journalinnehåll */}
        <div className="journal-content-panel">
          {/* Smal skärm: referensen ovanför editorn, hopfällbar med egen scroll */}
          {referenceEntryId != null && !isWide && renderReference('top')}
          {loadingContent ? (
            <div className="journal-content-loading">
              <i className="fas fa-spinner fa-spin"></i> Laddar...
            </div>
          ) : selectedJournals.length === 0 ? (
            <div className="journal-content-empty">
              <i className="fas fa-hand-pointer"></i>
              <p>Välj en journal</p>
              <span>Klicka på en journal i listan för att visa den</span>
              <span className="hint">Tips: Håll Shift och klicka för att välja flera</span>
            </div>
          ) : (
            <div className="journal-content-view">
              {selectedJournals.map((journal, index) => (
                <React.Fragment key={journal.entry_id}>
                  {index > 0 && <div className="journal-separator"></div>}
                  <JournalContent
                    journal={journal}
                    isEditing={editingJournalId === journal.entry_id}
                    onEdit={() => handleEdit(journal.entry_id)}
                    onSign={(content, version) => handleSign(journal.entry_id, content, version)}
                    onDelete={() => handleDelete(journal.entry_id)}
                    onCancel={handleCancelEdit}
                    saving={savingJournal}
                    autoRecord={autoRecordEntryId === journal.entry_id}
                    onAutoRecordConsumed={() => setAutoRecordEntryId(null)}
                    unpaidAppointments={unpaidAppointments}
                    onAppointmentPaid={() => loadSelectedJournals(selectedIds, { silent: true })}
                    onAutosaved={(saved) => handleAutosaved(journal.entry_id, saved)}
                  />
                </React.Fragment>
              ))}
            </div>
          )}
        </div>

        {/* Bred skärm: referensen som tredje kolumn */}
        {referenceEntryId != null && isWide && renderReference('side')}
      </div>

      {/* Modal för ny journal */}
      {showNewJournalModal && (
        <NewJournalModal
          patientId={patientId}
          patient={patient}
          appointmentId={modalAppointmentId}
          onClose={() => setShowNewJournalModal(false)}
          onCreated={handleJournalCreated}
        />
      )}

      {/* Bekräfta radering av utkast */}
      <ConfirmDialog
        isOpen={deleteTargetId !== null}
        variant="danger"
        title="Ta bort journal?"
        message="Utkastet tas bort permanent och kan inte återställas."
        confirmLabel="Ta bort"
        cancelLabel="Avbryt"
        loading={deleting}
        onConfirm={confirmDelete}
        onCancel={() => setDeleteTargetId(null)}
      />
    </div>
  );
};

export default JournalTab;
